import React, { useState } from 'react';
import Snackbar from '../../Snackbar/Snackbar';
import useDevices from '../../../hooks/useDevices/useDevices';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import { useIntl } from 'react-intl';

export function getSnackbarContent(hasAudio: boolean, hasVideo: boolean, error?: Error) {
  let headlineTranslationKey = '';
  let messageTranslationKey = '';

  switch (true) {
    // These custom errors are thrown by the useLocalTracks hook. They are thrown when the user explicitly denies
    // permission to only their camera, or only their microphone.
    case error?.message === 'CameraPermissionsDenied':
      headlineTranslationKey = 'error.media.camera_permission_denied.headline';
      messageTranslationKey = 'error.media.camera_permission_denied.message';
      break;
    case error?.message === 'MicrophonePermissionsDenied':
      headlineTranslationKey = 'error.media.mic_permission_denied.headline';
      messageTranslationKey = 'error.media.mic_permission_denied.message';
      break;

    // This error is emitted when the user or the user's system has denied permission to use the media devices
    case error?.name === 'NotAllowedError':
      headlineTranslationKey = 'error.media.not_allowed.headline';

      if (error!.message === 'Permission denied by system') {
        // Chrome only
        messageTranslationKey = 'error.media.not_allowed.operating_system_message';
      } else {
        messageTranslationKey = 'error.media.not_allowed.browser_message';
      }

      break;

    // This error is emitted when input devices are not connected or disabled in the OS settings
    case error?.name === 'NotFoundError':
      headlineTranslationKey = 'error.media.not_found.headline';
      messageTranslationKey = 'error.media.not_found.message';
      break;

    // Other getUserMedia errors are less likely to happen in this app. Here we will display
    // the system's error message directly to the user.
    case Boolean(error):
      headlineTranslationKey = 'error.media.other.headline';
      messageTranslationKey = `${error!.name} ${error!.message}`;
      break;

    case !hasAudio && !hasVideo:
      headlineTranslationKey = 'error.media.both_not_detected.headline';
      messageTranslationKey = 'error.media.both_not_detected.message';
      break;

    case !hasVideo:
      headlineTranslationKey = 'error.media.video_not_detected.headline';
      messageTranslationKey = 'error.media.video_not_detected.message';
      break;

    case !hasAudio:
      headlineTranslationKey = 'error.media.audio_not_detected.headline';
      messageTranslationKey = 'error.media.audio_not_detected.message';
  }

  return {
    headlineTranslationKey,
    messageTranslationKey,
  };
}

export default function MediaErrorSnackbar({ error }: { error?: Error }) {
  const { formatMessage } = useIntl();
  const { hasAudioInputDevices, hasVideoInputDevices } = useDevices();

  const { isAcquiringLocalTracks } = useVideoContext();

  const [isSnackbarDismissed, setIsSnackbarDismissed] = useState(false);

  const isSnackbarOpen =
    !isSnackbarDismissed &&
    !isAcquiringLocalTracks &&
    (Boolean(error) || !hasAudioInputDevices || !hasVideoInputDevices);

  const { headlineTranslationKey, messageTranslationKey } = getSnackbarContent(
    hasAudioInputDevices,
    hasVideoInputDevices,
    error
  );

  return (
    <Snackbar
      open={isSnackbarOpen}
      handleClose={() => setIsSnackbarDismissed(true)}
      headline={headlineTranslationKey && formatMessage({ id: headlineTranslationKey })}
      message={messageTranslationKey && formatMessage({ id: messageTranslationKey })}
      variant="warning"
    />
  );
}
